import { useEffect, useRef, useState } from 'react';

import documentsService from '../../domain/services/solicitation/documents.service';
import { abort } from '../../domain/services/upload/upload.session.service';
import { patchOfState, patchOfSettled } from '../../domain/services/upload/upload.rows';

const STALLED_REASON = 'UploadStalled';

export const stalledSessionsOf = (error) => {
    const data = error && error.response && error.response.data;
    return data && data.reason === STALLED_REASON && Array.isArray(data.sessions) ? data.sessions : null;
};

const rowOf = (session) => ({ ...session, status: 'stalled' });

const abortQuietly = (sessionId) => abort(sessionId).catch(() => null);

export default function useStalledUploads({ requestId, onResolved, onSent }) {
    const [rows, setRows] = useState([]);
    const [pendingResolve, setPendingResolve] = useState(false);
    const latest = useRef({ onResolved, onSent });
    latest.current = { onResolved, onSent };

    useEffect(() => {
        if (!pendingResolve || rows.length) return;
        setPendingResolve(false);
        latest.current.onResolved();
    }, [pendingResolve, rows]);

    const patch = (sessionId, change) => change && setRows(previous => previous.map(row => (row.sessionId === sessionId ? { ...row, ...change } : row)));

    const show = (sessions) => setRows(sessions.map(rowOf));

    const finishWithout = async () => {
        await Promise.all(rows.map(row => abortQuietly(row.sessionId)));
        setRows([]);
        setPendingResolve(true);
    };

    const resend = async (sessionId, file) => {
        patch(sessionId, { status: 'uploading', uploadProgress: 0, fileSize: file.size, failureMessage: null });
        const [outcome] = await documentsService.saveEach(requestId, [file], {
            onFileState: (_file, state) => patch(sessionId, patchOfState(state)),
            onFileSettled: (_file, settled) => patch(sessionId, patchOfSettled(settled)),
        });
        if (!outcome || outcome.status !== 'completed') return;
        await abortQuietly(sessionId);
        latest.current.onSent();
        setRows(previous => previous.filter(row => row.sessionId !== sessionId));
        setPendingResolve(true);
    };

    return { rows, show, resend, finishWithout };
}
